// src/tabs/calendar/_block-helpers.js — Phase 6.B PR5A (ADR-096)
// Helpers partagés TableauView + modals Create/Details.
// (MensuelView retirée PR #169d, vue Aujourd'hui ne consomme pas ces helpers
//  directement — passe par _today-helpers.js qui réutilise la convention block.)
// Mutualisation extraite suite à fix N-QA-1 (hisser useDailyPricing au parent)
// + PR #167 (formatPeriodLabel partagé Create + Details).

/**
 * Recherche le block actif couvrant une date ISO (premier match).
 *
 * Convention A (ADR-111, migration 20260601000001) : `end_date` est EXCLUSIVE
 * (jour de check-out, identique convention reservations). Un block du 02 au 05
 * couvre les nuits du 02, 03, 04 → le 05 est libre.
 *
 * @param {Array<{ id, start_date, end_date, reason, beds24_pushed }>|null} blocks
 * @param {string} dateISO — YYYY-MM-DD
 * @returns {object|null} block trouvé ou null
 */
export function findBlockForDate(blocks, dateISO) {
  if (!blocks || blocks.length === 0) return null
  for (const b of blocks) {
    // end_date EXCLUSIVE → jour de check-out non couvert (libre).
    if (b.start_date <= dateISO && b.end_date > dateISO) return b
  }
  return null
}

/**
 * Calcule les segments (barres continues) des blocks sur le mois affiché.
 *
 * BUG1 — remplace le rendu per-cell (1 icône par jour, illusion de plusieurs
 * voyageurs) par 1 barre horizontale continue par block. Même logique que les
 * barres résa (`bars` dans TableauView) :
 *   - Convention A (ADR-111) : `end_date` EXCLUSIVE (jour de check-out libre)
 *     → endLastIncluded = end_date − 1 jour.
 *   - Clamp sur le mois : start avant le 1er du mois → startCol 0 ;
 *     end après le dernier jour → endCol N−1.
 *   - Block hors mois (entièrement avant/après) → ignoré.
 *
 * Fonction PURE et testable : ne dépend que de `blocks` + `monthDays`.
 * `monthDays` = tableau de Date du mois affiché (1er → dernier), tel que
 * construit par le useMemo `monthDays` de TableauView.
 *
 * @param {Array<{ id, start_date, end_date, ... }>|null} blocks
 * @param {Array<Date>} monthDays — jours du mois courant (≥ 1 élément)
 * @returns {Array<{ block, startCol, endCol }>} 1 entrée par block visible
 *   (startCol/endCol 0-based dans monthDays).
 */
export function blocksToMonthSpans(blocks, monthDays) {
  if (!blocks || blocks.length === 0) return []
  if (!monthDays || monthDays.length === 0) return []

  const N = monthDays.length
  const monthFirst = monthDays[0]
  const monthLast = monthDays[N - 1]

  const out = []
  for (const b of blocks) {
    if (!b?.start_date || !b?.end_date) continue
    const s = new Date(b.start_date); s.setHours(0, 0, 0, 0)
    const e = new Date(b.end_date);   e.setHours(0, 0, 0, 0)

    // Convention A : barre de start_date inclus → end_date exclus.
    // → endLastIncluded = end_date − 1 jour.
    const endLastIncluded = new Date(e); endLastIncluded.setDate(endLastIncluded.getDate() - 1)

    // Block entièrement hors du mois affiché → skip.
    if (endLastIncluded < monthFirst || s > monthLast) continue

    const startCol = s < monthFirst ? 0 : (s.getDate() - 1)
    const endCol = endLastIncluded > monthLast ? (N - 1) : (endLastIncluded.getDate() - 1)
    if (endCol < startCol) continue

    out.push({ block: b, startCol, endCol })
  }
  return out
}

/**
 * Formate une période block en label utilisateur cohérent Create/Details.
 *
 * Convention A (ADR-111) : end_date EXCLUSIVE — jour de check-out, hors séjour.
 * Nuits = endDate - startDate (3 nuits pour 02→05). end == start est invalide
 * (0 nuit) ; la validation côté form refuse déjà ce cas.
 *
 * @param {{ startDate: string, endDate: string }} dates — ISO YYYY-MM-DD
 * @returns {string}
 *   - "Choisir une période" si dates manquantes
 *   - "Dates invalides (fin doit être après début)" si endDate <= startDate
 *   - "N nuit(s) bloquée(s) — checkin DD/MM/YYYY → checkout DD/MM/YYYY" sinon
 */
export function formatPeriodLabel({ startDate, endDate } = {}) {
  if (!startDate || !endDate) return "Choisir une période"
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
    return "Choisir une période"
  }
  // Convention A : end_date strictement après start_date (1 nuit = end = start+1).
  if (endDate <= startDate) return "Dates invalides (fin doit être après début)"
  const toFR = (iso) => {
    const [y, m, d] = iso.split("-")
    return `${d}/${m}/${y}`
  }
  const s = new Date(startDate); const e = new Date(endDate)
  const nights = Math.round((e - s) / 86400000)
  const label = nights === 1 ? "1 nuit bloquée" : `${nights} nuits bloquées`
  return `${label} — checkin ${toFR(startDate)} → checkout ${toFR(endDate)}`
}
